import { mm, type Um } from './units.js';
import type { HardwareItem, Issue, PanelPart, Vec3 } from './model.js';
import type { AssemblyInstance, AssemblyPanel, Front, FrontRect, FrontSpaceRef } from './document.js';
import { makePanel, partBox } from './geometry.js';
import { panelSize } from './assembly.js';
import { buildShelfSet } from './shelves.js';

/** Default gap between neighbouring fronts (fuga) — a setting of the assembly's front group. */
export const DEFAULT_FRONT_REVEAL = mm(3);
/** Default clearance of a front to the carcass edge / opening (zazor). */
export const DEFAULT_FRONT_CLEARANCE = mm(2);
/** Openings narrower than this (e.g. the side clearance of shelves) are not offered. */
const MIN_SPACE = mm(10);

/**
 * Fronts of free-panel assemblies. The front view (X/Y) of the panels that reach the front is split
 * into rectangular spaces (openings); a front covers one or several picked spaces. Spaces remember
 * the panels around them, so a front follows the carcass when it changes. A union of spaces that is
 * not a rectangle becomes a CNC front (its outline is listed separately in the cut list).
 */

const TOL = 500; // 0.5 mm
/** Panels whose front face is within this distance of the carcass front take part in the front view. */
const FRONT_REACH = mm(20);

interface Box {
  min: Vec3;
  max: Vec3;
}
const box = (p: AssemblyPanel): Box => {
  const s = panelSize(p);
  return { min: [...p.position], max: [p.position[0] + s[0], p.position[1] + s[1], p.position[2] + s[2]] };
};

export interface FrontSpace {
  ref: FrontSpaceRef;
  rect: FrontRect;
}

interface FrontView {
  zFront: Um;
  /** Panels that reach the front (keys and boxes). */
  solids: { key: string; b: Box }[];
  bbox: FrontRect;
}

/** Boxes of the generated shelves (key = shelf part key, e.g. `police-1-2`). */
function shelfBoxes(inst: AssemblyInstance): { key: string; b: Box }[] {
  const out: { key: string; b: Box }[] = [];
  for (const set of inst.shelfSets ?? [])
    for (const p of buildShelfSet(inst, set).parts) {
      const pb = partBox(p);
      out.push({ key: p.localKey, b: { min: pb.min, max: [pb.min[0] + pb.size[0], pb.min[1] + pb.size[1], pb.min[2] + pb.size[2]] } });
    }
  return out;
}

/** Box of a panel or a generated shelf by key. */
function solidBox(inst: AssemblyInstance, key: string | null): Box | null {
  if (!key) return null;
  const p = inst.panels.find((x) => x.key === key);
  if (p) return box(p);
  return shelfBoxes(inst).find((s) => s.key === key)?.b ?? null;
}

/** `betweenShelves`: generated shelves split the openings too. The outer box is taken from the panels only. */
function frontView(inst: AssemblyInstance, betweenShelves = false): FrontView | null {
  const all = inst.panels.filter((p) => !p.repeat || p.repeat.count <= 1).map((p) => ({ key: p.key, b: box(p) }));
  if (!all.length) return null;
  const zFront = Math.max(...all.map((s) => s.b.max[2]));
  const panels = all.filter((s) => s.b.max[2] >= zFront - FRONT_REACH);
  const solids = betweenShelves ? [...panels, ...shelfBoxes(inst).filter((s) => s.b.max[2] >= zFront - FRONT_REACH)] : panels;
  return {
    zFront,
    solids,
    bbox: { x0: Math.min(...panels.map((s) => s.b.min[0])), y0: Math.min(...panels.map((s) => s.b.min[1])), x1: Math.max(...panels.map((s) => s.b.max[0])), y1: Math.max(...panels.map((s) => s.b.max[1])) },
  };
}

const overlap = (a0: number, a1: number, b0: number, b1: number) => Math.min(a1, b1) - Math.max(a0, b0);

/**
 * Openings of the front view, as rectangles, with the panels that bound them. With `betweenShelves`
 * the generated shelves split them as well (one opening per shelf gap).
 */
export function frontSpaces(inst: AssemblyInstance, opts: { betweenShelves?: boolean } = {}): FrontSpace[] {
  const v = frontView(inst, !!opts.betweenShelves);
  if (!v) return [];
  const uniq = (xs: number[]) => [...new Set(xs.map((x) => Math.round(x)))].sort((a, b) => a - b);
  const xs = uniq([v.bbox.x0, v.bbox.x1, ...v.solids.flatMap((s) => [s.b.min[0], s.b.max[0]])]).filter((x) => x >= v.bbox.x0 && x <= v.bbox.x1);
  const ys = uniq([v.bbox.y0, v.bbox.y1, ...v.solids.flatMap((s) => [s.b.min[1], s.b.max[1]])]).filter((y) => y >= v.bbox.y0 && y <= v.bbox.y1);
  const solidAt = (x: number, y: number) => v.solids.some((s) => x > s.b.min[0] && x < s.b.max[0] && y > s.b.min[1] && y < s.b.max[1]);

  // Free cells merged along rows into strips, then strips with the same width stacked into rectangles.
  const rects: FrontRect[] = [];
  let open = new Map<string, FrontRect>();
  for (let j = 0; j < ys.length - 1; j++) {
    const y0 = ys[j]!;
    const y1 = ys[j + 1]!;
    if (y1 - y0 <= TOL) continue;
    const strips: [number, number][] = [];
    let start: number | null = null;
    for (let i = 0; i < xs.length - 1; i++) {
      const free = xs[i + 1]! - xs[i]! > TOL && !solidAt((xs[i]! + xs[i + 1]!) / 2, (y0 + y1) / 2);
      if (free && start === null) start = xs[i]!;
      if (!free && start !== null) {
        strips.push([start, xs[i]!]);
        start = null;
      }
    }
    if (start !== null) strips.push([start, xs[xs.length - 1]!]);
    const next = new Map<string, FrontRect>();
    for (const [x0, x1] of strips) {
      const k = `${x0}:${x1}`;
      const prev = open.get(k);
      if (prev && prev.y1 === y0) {
        prev.y1 = y1;
        next.set(k, prev);
        open.delete(k);
      } else next.set(k, { x0, x1, y0, y1 });
    }
    for (const r of open.values()) rects.push(r);
    open = next;
  }
  for (const r of open.values()) rects.push(r);
  const kept = rects.filter((r) => r.x1 - r.x0 >= MIN_SPACE && r.y1 - r.y0 >= MIN_SPACE);
  rects.length = 0;
  rects.push(...kept);
  rects.sort((a, b) => b.y0 - a.y0 || a.x0 - b.x0);

  const pick = (pred: (b: Box) => boolean, span: (b: Box) => number) => {
    let best: { key: string; o: number } | null = null;
    for (const s of v.solids) {
      if (!pred(s.b)) continue;
      const o = span(s.b);
      if (o > TOL && (!best || o > best.o)) best = { key: s.key, o };
    }
    return best?.key ?? null;
  };
  return rects.map((r) => ({
    rect: r,
    ref: {
      left: pick((b) => Math.abs(b.max[0] - r.x0) <= TOL, (b) => overlap(b.min[1], b.max[1], r.y0, r.y1)),
      right: pick((b) => Math.abs(b.min[0] - r.x1) <= TOL, (b) => overlap(b.min[1], b.max[1], r.y0, r.y1)),
      bottom: pick((b) => Math.abs(b.max[1] - r.y0) <= TOL, (b) => overlap(b.min[0], b.max[0], r.x0, r.x1)),
      top: pick((b) => Math.abs(b.min[1] - r.y1) <= TOL, (b) => overlap(b.min[0], b.max[0], r.x0, r.x1)),
      rect: { ...r },
    },
  }));
}

/** Current rectangle of a stored space (follows its bounding panels). */
function spaceRect(inst: AssemblyInstance, s: FrontSpaceRef): FrontRect {
  const b = (k: string | null) => solidBox(inst, k);
  const l = b(s.left);
  const r = b(s.right);
  const bo = b(s.bottom);
  const t = b(s.top);
  return { x0: l ? l.max[0] : s.rect.x0, x1: r ? r.min[0] : s.rect.x1, y0: bo ? bo.max[1] : s.rect.y0, y1: t ? t.min[1] : s.rect.y1 };
}

export interface FrontShape {
  /** Pieces of the front in the X/Y plane (assembly-local µm). */
  pieces: FrontRect[];
  bbox: FrontRect;
  /** Not a rectangle → CNC. */
  irregular: boolean;
  z0: Um;
  z1: Um;
}

/**
 * Outline of a front over the given spaces. Overlay: outer panel edges are covered up to the
 * clearance (zazor); an inner panel (divider, fixed shelf) is covered completely while there is no
 * front on its other side, and to its middle minus half the gap (fuga) once there is one. Joined
 * openings run over the panel between them. Inset: inside the opening with the clearance.
 */
export function frontShape(inst: AssemblyInstance, f: Pick<Front, 'spaces' | 'mode' | 'thickness'> & { id?: string; clearance?: Um; slice?: Front['slice'] }): FrontShape | null {
  const v = frontView(inst);
  if (!v || !f.spaces.length) return null;
  const rects = f.spaces.map((s) => spaceRect(inst, s));
  const thick = (k: string | null, axis: 0 | 1) => {
    const b = solidBox(inst, k);
    return b ? { t: b.max[axis] - b.min[axis], b } : null;
  };
  const gap = inst.frontReveal ?? DEFAULT_FRONT_REVEAL;
  const c = f.clearance ?? DEFAULT_FRONT_CLEARANCE;
  const others = (inst.fronts ?? []).filter((o) => o.id !== f.id);
  const pieces = f.spaces.map((s, i) => {
    const rect = rects[i]!;
    const side = (key: string | null, opposite: 'left' | 'right' | 'bottom' | 'top', axis: 0 | 1, isMax: boolean) => {
      // Another picked space on the far side of the same panel: the front runs over the panel.
      const joined = !!key && f.spaces.some((o, j) => j !== i && o[opposite] === key && (axis === 0 ? overlap(rects[j]!.y0, rects[j]!.y1, rect.y0, rect.y1) : overlap(rects[j]!.x0, rects[j]!.x1, rect.x0, rect.x1)) > TOL);
      const p = thick(key, axis);
      if (joined && p) return isMax ? p.t : 0;
      if (f.mode === 'inset' || !p) return -c;
      const outer = isMax ? p.b.max[axis] >= (axis === 0 ? v.bbox.x1 : v.bbox.y1) - TOL : p.b.min[axis] <= (axis === 0 ? v.bbox.x0 : v.bbox.y0) + TOL;
      if (outer) return p.t - c;
      // Inner panel: shared with a front on the other side → half each with the gap between them.
      const span = (sp: FrontSpaceRef) => {
        const o = spaceRect(inst, sp);
        return axis === 0 ? overlap(o.y0, o.y1, rect.y0, rect.y1) : overlap(o.x0, o.x1, rect.x0, rect.x1);
      };
      const neighbour = others.some((o) => o.mode === 'overlay' && o.spaces.some((sp) => sp[opposite] === key && span(sp) > TOL));
      return neighbour ? (p.t - gap) / 2 : p.t - c;
    };
    return {
      x0: Math.round(rect.x0 - side(s.left, 'right', 0, false)),
      x1: Math.round(rect.x1 + side(s.right, 'left', 0, true)),
      y0: Math.round(rect.y0 - side(s.bottom, 'top', 1, false)),
      y1: Math.round(rect.y1 + side(s.top, 'bottom', 1, true)),
    };
  });
  const full = bboxOf(pieces);
  const cut = f.slice ? sliceRect(full, f.slice, gap) : full;
  const parts = f.slice
    ? pieces
        .map((p) => ({ x0: Math.max(p.x0, cut.x0), y0: Math.max(p.y0, cut.y0), x1: Math.min(p.x1, cut.x1), y1: Math.min(p.y1, cut.y1) }))
        .filter((p) => p.x1 - p.x0 > TOL && p.y1 - p.y0 > TOL)
    : pieces;
  if (!parts.length) return null;
  const bbox = bboxOf(parts);
  const irregular = unionArea(parts) < (bbox.x1 - bbox.x0) * (bbox.y1 - bbox.y0) - TOL * TOL;
  const z0 = f.mode === 'inset' ? v.zFront - f.thickness : v.zFront;
  return { pieces: parts, bbox, irregular, z0, z1: z0 + f.thickness };
}

const bboxOf = (rs: FrontRect[]): FrontRect => ({ x0: Math.min(...rs.map((p) => p.x0)), y0: Math.min(...rs.map((p) => p.y0)), x1: Math.max(...rs.map((p) => p.x1)), y1: Math.max(...rs.map((p) => p.y1)) });

/**
 * Part of a split front: fractions [start, end] of the whole front per axis; every inner cut leaves
 * the gap (fuga), so equal fractions give equal fronts.
 */
function sliceRect(r: FrontRect, slice: NonNullable<Front['slice']>, gap: Um): FrontRect {
  const cut = (a0: number, a1: number, s?: [number, number]): [number, number] => {
    if (!s) return [a0, a1];
    const span = a1 - a0 + gap;
    return [Math.round(a0 + s[0] * span), Math.round(a0 + s[1] * span - gap)];
  };
  const [x0, x1] = cut(r.x0, r.x1, slice.x);
  const [y0, y1] = cut(r.y0, r.y1, slice.y);
  return { x0, x1, y0, y1 };
}

/**
 * Splits a front in two: left/right (vertical cut) or bottom/top (horizontal cut). The original
 * front becomes the left/bottom part (keeps its id), a new front is the other part. Both keep the
 * openings, so they still follow the carcass. Hinges: left part left, right part right.
 */
export function splitFront(inst: AssemblyInstance, frontId: string, direction: 'vertical' | 'horizontal'): Front | null {
  const f = inst.fronts?.find((x) => x.id === frontId);
  if (!f) return null;
  const axis = direction === 'vertical' ? 'x' : 'y';
  const [s, e] = f.slice?.[axis] ?? [0, 1];
  const mid = (s + e) / 2;
  const other: Front = { ...structuredClone(f), id: nextFrontId(inst), slice: { ...f.slice, [axis]: [mid, e] } };
  f.slice = { ...f.slice, [axis]: [s, mid] };
  if (direction === 'vertical') {
    f.label = `${f.label} L`;
    other.label = `${other.label} D`;
    f.hinge = 'left';
    other.hinge = 'right';
  } else {
    f.label = `${f.label} dolje`;
    other.label = `${other.label} gore`;
  }
  inst.fronts!.splice(inst.fronts!.indexOf(f) + 1, 0, other);
  return other;
}

/** Area covered by axis-aligned rectangles (overlaps counted once). */
function unionArea(rs: FrontRect[]): number {
  const xs = [...new Set(rs.flatMap((r) => [r.x0, r.x1]))].sort((a, b) => a - b);
  const ys = [...new Set(rs.flatMap((r) => [r.y0, r.y1]))].sort((a, b) => a - b);
  let area = 0;
  for (let i = 0; i < xs.length - 1; i++)
    for (let j = 0; j < ys.length - 1; j++) {
      const cx = (xs[i]! + xs[i + 1]!) / 2;
      const cy = (ys[j]! + ys[j + 1]!) / 2;
      if (rs.some((r) => cx > r.x0 && cx < r.x1 && cy > r.y0 && cy < r.y1)) area += (xs[i + 1]! - xs[i]!) * (ys[j + 1]! - ys[j]!);
    }
  return area;
}

export function nextFrontId(inst: AssemblyInstance): string {
  const used = new Set([...inst.panels.map((p) => p.key), ...(inst.shelfSets ?? []).map((s) => s.id), ...(inst.fronts ?? []).map((f) => f.id)]);
  let n = (inst.fronts ?? []).length + 1;
  while (used.has(`fronta-${n}`)) n++;
  return `fronta-${n}`;
}

export function makeFront(inst: AssemblyInstance, spaces: FrontSpace[]): Front {
  const n = (inst.fronts ?? []).length + 1;
  return {
    id: nextFrontId(inst),
    label: n === 1 ? 'Fronta' : `Fronta ${n}`,
    spaces: spaces.map((s) => structuredClone(s.ref)),
    mode: 'overlay',
    clearance: DEFAULT_FRONT_CLEARANCE,
    thickness: mm(18),
    hinge: null,
    materialSlot: 'front',
    grain: 'length',
  };
}

/** Generic hinge count by the length of the hinged edge. */
export function hingeCount(edge: Um): number {
  return edge <= mm(900) ? 2 : edge <= mm(1600) ? 3 : edge <= mm(2000) ? 4 : 5;
}

export function buildFront(inst: AssemblyInstance, f: Front): { parts: PanelPart[]; hardware: HardwareItem[]; issues: Issue[] } {
  const issues: Issue[] = [];
  const say = (message: string, severity: 'warning' | 'error' | 'info' = 'warning') => issues.push({ severity, code: 'fronts', message: `${f.label}: ${message}`, cabinetId: inst.id, partId: `${inst.id}/${f.id}` });
  for (const s of f.spaces) for (const k of [s.left, s.right, s.bottom, s.top]) if (k && !inst.panels.some((p) => p.key === k)) say(`ploča „${k}” oko prostora više ne postoji — uzeta je zapamćena mjera.`);
  const shape = frontShape(inst, f);
  if (!shape) return { parts: [], hardware: [], issues };
  const { bbox } = shape;
  const w = bbox.x1 - bbox.x0;
  const h = bbox.y1 - bbox.y0;
  if (w <= 0 || h <= 0) {
    say('fronta nema površinu.', 'error');
    return { parts: [], hardware: [], issues };
  }
  if (shape.irregular) say('nepravilan oblik — fronta ide na CNC obradu, njene mjere su posebno u krojnoj listi.');
  if (f.hinge === null) say('izaberite stranu šarki.', 'info');
  const part = makePanel({
    cabinetId: inst.id,
    localKey: f.id,
    role: 'door',
    label: f.label,
    box: { min: [bbox.x0, bbox.y0, shape.z0], size: [w, h, f.thickness] },
    lengthAxis: 'y',
    widthAxis: 'x',
    materialSlot: f.materialSlot,
    grain: f.grain,
    ...(f.hinge === 'left' ? { movable: 'hinge-left' as const } : f.hinge === 'right' ? { movable: 'hinge-right' as const } : {}),
  });
  if (shape.irregular) part.cnc = { outline: shape.pieces.map((p) => ({ x0: p.x0 - bbox.x0, y0: p.y0 - bbox.y0, x1: p.x1 - bbox.x0, y1: p.y1 - bbox.y0 })) };
  if (f.hinge && f.hinge !== 'none') part.hingeSide = f.hinge;
  const hardware: HardwareItem[] = [];
  if (f.hinge && f.hinge !== 'none') {
    const edge = f.hinge === 'left' || f.hinge === 'right' ? h : w;
    hardware.push({ id: `${inst.id}/${f.id}-hinges`, cabinetId: inst.id, kind: 'hinge', label: 'Šarka (generička)', quantity: hingeCount(edge), generic: true, note: 'Broj šarki po dužini ivice (≤900 mm: 2, ≤1600: 3, ≤2000: 4); provjeriti prema modelu šarke i masi fronte.' });
  }
  return { parts: [part], hardware, issues };
}

export type FrontHinge = NonNullable<Front['hinge']>;
export const HINGE_LABEL: Record<FrontHinge, string> = { left: 'Lijevo', right: 'Desno', top: 'Gore', bottom: 'Dolje', none: 'Bez šarki' };

/** Which front a part local key is. */
export function frontOfPart(inst: AssemblyInstance, localKey: string): Front | null {
  return (inst.fronts ?? []).find((f) => f.id === localKey) ?? null;
}

/**
 * "+ Pregrada": a plain panel that cuts an opening in the middle — vertical (a divider, thickness on
 * X) or horizontal (a fixed shelf, thickness on Y). Its depth is the depth shared by the panels
 * around the opening. After creation it is an ordinary panel.
 */
export function makeDivider(inst: AssemblyInstance, space: FrontSpace, direction: 'vertical' | 'horizontal', thickness: Um = mm(18)): AssemblyPanel {
  const rect = spaceRect(inst, space.ref);
  const around = [space.ref.left, space.ref.right, space.ref.bottom, space.ref.top].map((k) => solidBox(inst, k)).filter((b): b is Box => !!b);
  const all = inst.panels.map(box);
  const z0 = around.length ? Math.max(...around.map((b) => b.min[2])) : Math.min(...all.map((b) => b.min[2]));
  const z1 = around.length ? Math.min(...around.map((b) => b.max[2])) : Math.max(...all.map((b) => b.max[2]));
  const used = new Set([...inst.panels.map((p) => p.key), ...(inst.shelfSets ?? []).map((s) => s.id), ...(inst.fronts ?? []).map((f) => f.id)]);
  let n = inst.panels.length + 1;
  while (used.has(`ploca-${n}`)) n++;
  const base = {
    key: `ploca-${n}`,
    materialSlot: 'carcass' as const,
    grain: 'length' as const,
    repeat: null,
    joints: [],
    thickness,
    width: z1 - z0,
  };
  if (direction === 'vertical') {
    const x = Math.round((rect.x0 + rect.x1) / 2 - thickness / 2);
    return { ...base, label: 'Pregrada', orientation: 'vertical-side', length: rect.y1 - rect.y0, position: [x, rect.y0, z0] };
  }
  const y = Math.round((rect.y0 + rect.y1) / 2 - thickness / 2);
  return { ...base, label: 'Fiksna polica', orientation: 'horizontal', length: rect.x1 - rect.x0, position: [rect.x0, y, z0] };
}
